/** Typy s nečíselnými parametry (DuckDB) — kanonizují se beze změny obsahu. */
const COMPLEX_TYPE_PREFIXES = ['struct', 'map', 'union'];
/**
 * Prostorové typy (PostGIS `geometry`/`geography`) — parametr je poddruh
 * geometrie + volitelné SRID, např. `geometry(Point,4326)`, ne číslo, takže
 * je `parseTypeString` nerozloží. Bez parametrů (`geometry`, `geography`)
 * jde o obyčejnou katalogovou položku (viz `postgresTypeCatalog`).
 */
const SPATIAL_TYPE_PREFIXES = ['geometry', 'geography'];
export function parseTypeString(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    let working = trimmed;
    let arrayDepth = 0;
    while (working.endsWith('[]')) {
        arrayDepth += 1;
        working = working.slice(0, -2).trimEnd();
    }
    const openIndex = working.indexOf('(');
    if (openIndex === -1) {
        return { baseName: normalizeName(working), params: [], arrayDepth };
    }
    if (!working.endsWith(')'))
        return null;
    const baseName = normalizeName(working.slice(0, openIndex));
    const paramText = working.slice(openIndex + 1, -1).trim();
    if (!baseName)
        return null;
    if (!paramText)
        return { baseName, params: [], arrayDepth };
    const params = [];
    for (const part of paramText.split(',')) {
        const value = Number.parseInt(part.trim(), 10);
        if (!Number.isFinite(value) || String(value) !== part.trim())
            return null;
        params.push(value);
    }
    return { baseName, params, arrayDepth };
}
function normalizeName(value) {
    return value.trim().toLowerCase().replace(/\s+/g, ' ');
}
export function formatType(baseName, params, arrayDepth) {
    const paramPart = params.length > 0 ? `(${params.join(',')})` : '';
    return `${baseName}${paramPart}${'[]'.repeat(arrayDepth)}`;
}
/** Index kanonických názvů i aliasů → deskriptor typu. */
export function buildTypeIndex(catalog) {
    const index = new Map();
    for (const type of catalog.types) {
        index.set(normalizeName(type.name), type);
        for (const alias of type.aliases)
            index.set(normalizeName(alias), type);
    }
    return index;
}
export function mapTypeWithCatalog(index, rawType) {
    const parsed = parseTypeString(rawType);
    if (!parsed) {
        const complex = mapComplexType(rawType) ?? mapSpatialType(rawType);
        if (complex)
            return complex;
        return { canonical: null, recognized: false, baseType: null };
    }
    const descriptor = index.get(parsed.baseName);
    if (!descriptor) {
        return { canonical: null, recognized: false, baseType: parsed.baseName };
    }
    return {
        canonical: formatType(descriptor.name, parsed.params, parsed.arrayDepth),
        recognized: true,
        baseType: descriptor.name,
    };
}
/**
 * `STRUCT(...)`, `MAP(...)`, `UNION(...)` mají nečíselné parametry — parser je
 * nerozloží, ale jde o plnohodnotné typy dialektu, ne o neznámý typ.
 */
function mapComplexType(rawType) {
    const normalized = rawType.trim();
    const lower = normalized.toLowerCase();
    const prefix = COMPLEX_TYPE_PREFIXES.find((candidate) => lower.startsWith(`${candidate}(`));
    if (!prefix || !normalized.endsWith(')'))
        return null;
    return { canonical: normalized, recognized: true, baseType: prefix };
}
/** `geometry(Point,4326)`, `geography(MultiPolygon,4326)` apod. — viz `SPATIAL_TYPE_PREFIXES`. */
function mapSpatialType(rawType) {
    const normalized = rawType.trim();
    const lower = normalized.toLowerCase();
    const prefix = SPATIAL_TYPE_PREFIXES.find((candidate) => lower.startsWith(`${candidate}(`));
    if (!prefix || !normalized.endsWith(')'))
        return null;
    return { canonical: normalized, recognized: true, baseType: prefix };
}
export function validateTypeWithCatalog(index, typeName) {
    if (!typeName.trim())
        return { valid: false, reason: 'empty' };
    const parsed = parseTypeString(typeName);
    if (!parsed) {
        return mapComplexType(typeName) ?? mapSpatialType(typeName)
            ? { valid: true }
            : { valid: false, reason: 'unknownType', detail: typeName };
    }
    const descriptor = index.get(parsed.baseName);
    if (!descriptor)
        return { valid: false, reason: 'unknownType', detail: parsed.baseName };
    switch (descriptor.parameters) {
        case 'none':
            if (parsed.params.length > 0) {
                return { valid: false, reason: 'parameterNotAllowed', detail: descriptor.name };
            }
            return { valid: true };
        case 'length':
            if (parsed.params.length > 1) {
                return { valid: false, reason: 'parameterInvalid', detail: descriptor.name };
            }
            if (parsed.params.length === 1 && parsed.params[0] <= 0) {
                return { valid: false, reason: 'parameterInvalid', detail: descriptor.name };
            }
            return { valid: true };
        case 'precisionScale': {
            if (parsed.params.length > 2) {
                return { valid: false, reason: 'parameterInvalid', detail: descriptor.name };
            }
            const precision = parsed.params[0];
            const scale = parsed.params[1];
            if (precision !== undefined && precision <= 0) {
                return { valid: false, reason: 'parameterInvalid', detail: descriptor.name };
            }
            if (scale !== undefined && (scale < 0 || (precision !== undefined && scale > precision))) {
                return { valid: false, reason: 'parameterInvalid', detail: descriptor.name };
            }
            return { valid: true };
        }
        default:
            return { valid: true };
    }
}
//# sourceMappingURL=typeCatalog.js.map